<template>
  <div class="logo-upload-section" :class="{ 'has-image': hasImage }">
    <AdImagePicker
      :imageUrl="displayImageUrl"
      :uploading="isUploading"
      :hideDelete="!hasImage"
      :boxSize="hasImage"
      :width="hasImage ? undefined : '100%'"
      :height="hasImage ? undefined : 120"
      @click="triggerFileInput"
      @remove="handleRemove"
      @fileDrop="handleFileDrop" />
    <input
      type="file"
      ref="fileInput"
      accept="image/png,image/svg+xml,image/webp,image/jpeg,image/gif"
      class="d-none"
      @change="onFileSelected" />
    <span v-if="fileError" class="text-danger small mt-1" role="alert">{{ fileError }}</span>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import AdImagePicker from '@/components/form/AdImagePicker.vue';
import translator from '@/locale/translator';
import { detectSvgDensity } from '@/utils/svgDensity';

const SVG_TYPE = 'image/svg+xml';
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/webp', SVG_TYPE, 'image/jpeg', 'image/gif'];
const MAX_LOGO_BYTES = 2_000_000;

const props = defineProps<{
  existingLogoUrl?: string;
}>();

const emit = defineEmits<{
  fileSelected: [file: File, pixelDensity?: number];
  remove: [];
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const pendingFile = ref<File | null>(null);
const pendingUrl = ref<string | null>(null);
const fileError = ref('');

const hasImage = computed(() => {
  return !!props.existingLogoUrl || !!pendingUrl.value;
});

const displayImageUrl = computed(() => {
  return pendingUrl.value || props.existingLogoUrl || null;
});

async function isWellFormedSvg(file: File): Promise<boolean> {
  const markup = await file.text();
  const doc = new DOMParser().parseFromString(markup, SVG_TYPE);
  return !doc.querySelector('parsererror') && doc.documentElement.nodeName.toLowerCase() === 'svg';
}

async function validateFile(file: File): Promise<string> {
  if (!ACCEPTED_LOGO_TYPES.includes(file.type)) return translator.get('registration.company.logo_error_type');
  if (file.size > MAX_LOGO_BYTES) return translator.get('registration.company.logo_error_size');
  if (file.type === SVG_TYPE && !(await isWellFormedSvg(file))) {
    return translator.get('registration.company.logo_error_svg');
  }
  return '';
}

const triggerFileInput = () => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const onFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  const file = target.files[0];
  if (fileInput.value) fileInput.value.value = '';

  fileError.value = '';
  const error = await validateFile(file);
  if (error) {
    fileError.value = error;
    return;
  }

  const pixelDensity = file.type === SVG_TYPE ? detectSvgDensity(await file.text()) : undefined;
  pendingFile.value = file;
  pendingUrl.value = URL.createObjectURL(file);
  emit('fileSelected', file, pixelDensity);
};

const handleFileDrop = async (file: File) => {
  if (isUploading.value) return;

  fileError.value = '';
  const error = await validateFile(file);
  if (error) {
    fileError.value = error;
    return;
  }

  const pixelDensity = file.type === SVG_TYPE ? detectSvgDensity(await file.text()) : undefined;
  pendingFile.value = file;
  pendingUrl.value = URL.createObjectURL(file);
  emit('fileSelected', file, pixelDensity);
};

const handleRemove = () => {
  if (pendingUrl.value) {
    URL.revokeObjectURL(pendingUrl.value);
  }
  pendingFile.value = null;
  pendingUrl.value = null;
  emit('remove');
};

watch(
  () => props.existingLogoUrl,
  () => {
    if (pendingUrl.value) {
      URL.revokeObjectURL(pendingUrl.value);
    }
    pendingFile.value = null;
    pendingUrl.value = null;
  }
);

defineExpose({
  pendingFile,
  isUploading,
  reset: () => {
    if (pendingUrl.value) {
      URL.revokeObjectURL(pendingUrl.value);
    }
    pendingFile.value = null;
    pendingUrl.value = null;
  },
});
</script>

<style scoped lang="scss">
.logo-upload-section {
  margin-top: 16px;

  &.has-image {
    display: flex;
    justify-content: center;
  }
}
</style>
